import React, { useState } from "react";
import styled from "styled-components";
import { func, object as obj, bool, string } from "prop-types";

import Button from "components/shared/Button";
import OnboardingInput from "components/onboarding/components/OnboardingInput";
import OnboardingCheckbox from "components/onboarding/components/OnboardingCheckbox";
import OnboardingSelect from "components/onboarding/components/OnboardingSelect";
import SuccessPage from "components/onboarding/form_pages/SuccessPage";
import {
  validateField,
  validateFields
} from "components/onboarding/OnboardingHelper";

import LoadingSpinner from "components/utils/LoadingSpinner";
import { splitFullName } from "utils";
import ExitButton from "components/shared/ExitButton";
import Copy from "components/shared/Copy";

const ButtonContainer = styled.div`
  text-align: right;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: baseline;

  @media (min-width: ${({ theme }) => theme.screen.tablet}px) {
    margin: 0 2rem;
  }
`;

const OnboardingFormPopup = styled.div`
  background: white;
  padding: 2rem 0.5rem;
  width: 100vw;
  transform: translateY(100vh);
  animation: ${({ formClosing }) =>
    formClosing ? "float-out 1s forwards" : "float-in 1s forwards"};
  ${({ isMobile }) =>
    isMobile &&
    `
      min-height: ${window.innerHeight}px;
      position: fixed;
      overflow-y: scroll;
      top: 0;
      bottom: 0;
    `}
  

  @media (min-width: ${({ theme }) => theme.screen.tablet}px) {
    animation: float-in 1s forwards;
    border-radius: 10px;
    max-width: 550px;
    min-height: 400px;
  }

  @keyframes float-in {
    from {
      transform: translateY(100vh);
    }
    to {
      transform: translateY(0);
    }
  }

  @keyframes float-out {
    from {
      transform: translateY(0);
    }
    to {
      transform: translateY(100vh);
    }
  }
`;

const OnboardingFormWrapper = styled.form`
  text-align: center;
`;

const OnboardingFormStandard = ({
  formData,
  isMobile,
  loading,
  magicToken,
  setFormData,
  submit,
  taxonomies,
  toggleFormOpenState
}) => {
  const [invalidMessages, setInvalidMessages] = useState({});
  const [formClosing, setFormClosing] = useState(false);

  const closeForm = () => {
    setFormClosing(true);
    setTimeout(() => {
      setFormClosing(false);
      toggleFormOpenState(false);
    }, 1000);
  };

  const fieldValidation = async e => {
    let { name, value } = e.target;
    if (name === "name") {
      name = "lastname";
      value = splitFullName(value).lastname;
    }
    const invalidMessage = await validateField(name, value);
    setInvalidMessages({ ...invalidMessages, [name]: invalidMessage });
  };

  const handleChange = async e => {
    const formField = e.target.name;
    const formValue =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    if (formField === "name") {
      setFormData({
        ...formData,
        name: e.target.value,
        ...splitFullName(e.target.value)
      });
    } else {
      setFormData({ ...formData, [formField]: formValue });
    }
  };

  const preSubmit = async event => {
    event.preventDefault();
    const validationFields = Object.keys(formData).filter(
      field => field !== "newsLetterSignup"
    );
    const checkValidation = await validateFields(formData, validationFields);
    if (Object.values(checkValidation).some(invalid => invalid)) {
      setInvalidMessages(checkValidation);
    } else {
      submit(formData);
    }
  };

  return (
    <OnboardingFormPopup
      className="onboarding-form"
      formClosing={formClosing}
      isMobile={isMobile}
      data-testid="onboarding-form"
    >
      {toggleFormOpenState && (
        <ExitButton
          fill="#3f83ee"
          onClick={() => (isMobile ? closeForm() : toggleFormOpenState(false))}
        />
      )}
      {magicToken ? (
        <SuccessPage standard />
      ) : (
        <OnboardingFormWrapper
          className="onboarding-form-fields"
          onSubmit={preSubmit}
        >
          <Copy color="darkBlue" size={1} weight="600">
            Share your details
          </Copy>
          <Copy margin="1rem auto" size={0.8}>
            Our team will put you in touch with local care providers to meet
            your needs
          </Copy>
          <OnboardingInput
            error={invalidMessages.lastname}
            name="name"
            placeholder="Full name"
            onBlur={fieldValidation}
            onChange={handleChange}
            type="text"
            value={formData.name}
            width="290px"
          />
          <OnboardingSelect
            error={invalidMessages.requested_care_type}
            name="requested_care_type"
            placeholder="Select care type"
            onChange={handleChange}
            options={taxonomies.requested_care_type}
            value={formData.requested_care_type}
            width="290px"
          />
          <OnboardingSelect
            error={invalidMessages.relationship}
            name="relationship"
            placeholder="Care is for"
            onChange={handleChange}
            options={taxonomies.care_for}
            value={formData.relationship}
            width="290px"
          />
          <OnboardingInput
            error={invalidMessages.phone_number}
            name="phone_number"
            placeholder="Phone number"
            onBlur={fieldValidation}
            onChange={handleChange}
            type="tel"
            value={formData.phone_number}
            width="290px"
          />
          <OnboardingInput
            error={invalidMessages.email}
            name="email"
            placeholder="Email address"
            onBlur={fieldValidation}
            onChange={handleChange}
            type="email"
            value={formData.email}
            width="290px"
          />
          <CheckboxContainer>
            <OnboardingCheckbox
              name="newsLetterSignup"
              checked={!!formData.newsLetterSignup}
              className="smallText"
              label="I am happy to receive occasional marketing emails"
              onChange={handleChange}
            />
            <OnboardingCheckbox
              error={invalidMessages.confirmed_tos}
              name="confirmed_tos"
              checked={!!formData.confirmed_tos}
              className="smallText"
              label={
                <div>
                  I agree to the SuperCarers{" "}
                  <a href="/privacy/" target="_blank">
                    privacy policy
                  </a>{" "}
                  and{" "}
                  <a href="/terms/" target="_blank">
                    terms and conditions
                  </a>
                  .
                </div>
              }
              onChange={handleChange}
            />
          </CheckboxContainer>
          <ButtonContainer>
            <Button
              background="basicBlue"
              className="onboarding__postcode-submit-standard"
              label={loading ? <LoadingSpinner /> : "Send"}
              type="submit"
              width="100px"
            />
          </ButtonContainer>
        </OnboardingFormWrapper>
      )}
    </OnboardingFormPopup>
  );
};

OnboardingFormStandard.propTypes = {
  formData: obj.isRequired,
  isMobile: bool,
  loading: bool.isRequired,
  magicToken: string,
  setFormData: func.isRequired,
  submit: func.isRequired,
  taxonomies: obj.isRequired,
  toggleFormOpenState: func
};

export default OnboardingFormStandard;
